/**
 * AddWarehouseStorageAccountForm Component
 *
 * Form to add a storage account mapping to a warehouse.
 * Part of Phase 8: Storage Mapping extension for warehouses.
 */

import React, { useState, useMemo } from 'react';
import { useStorageAccounts, StorageAccount } from '../../../../services/storageAccountApi';
import { useAddWarehouseStorageAccount, useWarehouseStorage, StorageAccountPurpose } from '../../hooks/useWarehouseStorage';

interface AddWarehouseStorageAccountFormProps {
  warehouseId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

// Purpose options with descriptions
const purposeOptions: Array<{
  value: StorageAccountPurpose;
  label: string;
  description: string;
}> = [
  {
    value: 'primary',
    label: 'Primary',
    description: 'Main storage location for backups. Required for backup operations.',
  },
  {
    value: 'secondary',
    label: 'Secondary',
    description: 'Failover storage for redundancy. Used if primary is unavailable.',
  },
  {
    value: 'archive',
    label: 'Archive',
    description: 'Long-term retention storage. Typically uses cold/archive tier.',
  },
  {
    value: 'overflow',
    label: 'Overflow',
    description: 'Additional storage when primary reaches capacity threshold.',
  },
];

export function AddWarehouseStorageAccountForm({
  warehouseId,
  onSuccess,
  onCancel,
}: AddWarehouseStorageAccountFormProps): React.ReactElement {
  // Fetch all storage accounts
  const { data: allStorageAccounts, isLoading: loadingAccounts } = useStorageAccounts(
    { status: 'active' }
  );

  // Fetch already mapped storage accounts for this warehouse
  const { data: existingMappings, isLoading: loadingMappings } = useWarehouseStorage(warehouseId);

  // Add storage account mutation
  const addStorage = useAddWarehouseStorageAccount(warehouseId);

  // Form state
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [purpose, setPurpose] = useState<StorageAccountPurpose>('primary');
  const [setAsPrimary, setSetAsPrimary] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter out already-mapped storage accounts
  const availableAccounts = useMemo(() => {
    if (!allStorageAccounts) return [];

    const mappedIds = new Set(existingMappings?.map((m) => m.storageAccountId) || []);
    return allStorageAccounts.filter((account) => !mappedIds.has(account.id));
  }, [allStorageAccounts, existingMappings]);

  // Filter by search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm.trim()) return availableAccounts;

    const term = searchTerm.toLowerCase();
    return availableAccounts.filter(
      (account) =>
        account.name.toLowerCase().includes(term) ||
        account.location?.toLowerCase().includes(term) ||
        account.resource_group?.toLowerCase().includes(term)
    );
  }, [availableAccounts, searchTerm]);

  // Get selected account details
  const selectedAccount = useMemo(
    () => availableAccounts.find((a) => a.id === selectedAccountId),
    [availableAccounts, selectedAccountId]
  );

  // Check if there's already a primary mapping
  const hasPrimaryMapping = useMemo(
    () => existingMappings?.some((m) => m.isPrimary && m.status === 'active'),
    [existingMappings]
  );

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAccountId) {
      return;
    }

    try {
      await addStorage.mutateAsync({
        storageAccountId: selectedAccountId,
        purpose,
        isPrimary: setAsPrimary && !hasPrimaryMapping,
      });
      onSuccess();
    } catch {
      // Error handled by mutation
    }
  };

  const isLoading = loadingAccounts || loadingMappings;

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      {/* Storage Account Selection */}
      <div>
        <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
          Storage Account
          <span className='text-red-500 ml-1'>*</span>
        </label>

        {/* Search Input */}
        <div className='relative mb-3'>
          <input
            type='text'
            placeholder='Search storage accounts...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-[var(--color-bg-card)] text-[var(--color-text-primary)]'
          />
          <svg
            className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--color-text-secondary)]'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className='animate-pulse space-y-2'>
            <div className='h-16 bg-[var(--color-bg-subtle)] rounded'></div>
            <div className='h-16 bg-[var(--color-bg-subtle)] rounded'></div>
            <div className='h-16 bg-[var(--color-bg-subtle)] rounded'></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredAccounts.length === 0 && (
          <div className='text-center py-8 text-[var(--color-text-secondary)] border-2 border-dashed border-[var(--color-border)] rounded-lg'>
            <svg
              className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'
              />
            </svg>
            <p className='mt-2 text-sm'>
              {searchTerm
                ? 'No storage accounts match your search.'
                : 'No available storage accounts found.'}
            </p>
            {availableAccounts.length === 0 && allStorageAccounts && allStorageAccounts.length > 0 && (
              <p className='mt-1 text-xs'>
                All storage accounts are already mapped to this warehouse.
              </p>
            )}
          </div>
        )}

        {/* Storage Account List */}
        {!isLoading && filteredAccounts.length > 0 && (
          <div className='max-h-64 overflow-y-auto border border-[var(--color-border)] rounded-lg divide-y divide-[var(--color-border)]'>
            {filteredAccounts.map((account) => (
              <label
                key={account.id}
                className={`flex items-start p-3 cursor-pointer hover:bg-[var(--color-bg-subtle)] transition-colors ${
                  selectedAccountId === account.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                    : ''
                }`}
              >
                <input
                  type='radio'
                  name='storageAccount'
                  value={account.id}
                  checked={selectedAccountId === account.id}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[var(--color-border)]'
                />
                <div className='ml-3 flex-1 min-w-0'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-[var(--color-text-primary)] truncate'>
                      {account.name}
                    </span>
                    {account.sku_tier && (
                      <span className='ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'>
                        {account.sku_tier}
                      </span>
                    )}
                  </div>
                  <div className='mt-1 flex items-center gap-3 text-xs text-[var(--color-text-secondary)]'>
                    <span>{account.location}</span>
                    <span>&#183;</span>
                    <span className='truncate'>{account.resource_group}</span>
                  </div>
                  {account.capacity_gb !== undefined && (
                    <div className='mt-1 text-xs text-[var(--color-text-secondary)]'>
                      {account.used_capacity_gb !== undefined && (
                        <>
                          <span>
                            {account.used_capacity_gb.toFixed(1)} / {account.capacity_gb} GB used
                          </span>
                          {account.used_percent !== undefined && (
                            <span className='ml-2'>({account.used_percent.toFixed(0)}%)</span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Purpose Selection */}
      <div>
        <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
          Storage Purpose
        </label>
        <div className='space-y-2'>
          {purposeOptions.map((option) => (
            <label
              key={option.value}
              className={`flex items-start p-3 border rounded-lg cursor-pointer hover:bg-[var(--color-bg-subtle)] transition-colors ${
                purpose === option.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-[var(--color-border)]'
              }`}
            >
              <input
                type='radio'
                name='purpose'
                value={option.value}
                checked={purpose === option.value}
                onChange={(e) => setPurpose(e.target.value as StorageAccountPurpose)}
                className='mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[var(--color-border)]'
              />
              <div className='ml-3'>
                <span className='text-sm font-medium text-[var(--color-text-primary)]'>
                  {option.label}
                </span>
                <p className='text-xs text-[var(--color-text-secondary)] mt-0.5'>
                  {option.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Set as Primary Checkbox (only if no primary exists) */}
      {!hasPrimaryMapping && (
        <div className='flex items-start'>
          <input
            type='checkbox'
            id='setAsPrimary'
            checked={setAsPrimary}
            onChange={(e) => setSetAsPrimary(e.target.checked)}
            className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[var(--color-border)] rounded'
          />
          <label htmlFor='setAsPrimary' className='ml-2'>
            <span className='text-sm font-medium text-[var(--color-text-primary)]'>
              Set as primary storage
            </span>
            <p className='text-xs text-[var(--color-text-secondary)]'>
              The primary storage account is used by default for all backup operations.
            </p>
          </label>
        </div>
      )}

      {/* Warning: No primary selected */}
      {!hasPrimaryMapping && !setAsPrimary && (
        <div className='flex items-start p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg'>
          <svg
            className='h-5 w-5 text-amber-500 mt-0.5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
            />
          </svg>
          <div className='ml-3'>
            <p className='text-sm text-amber-800 dark:text-amber-200'>
              <strong>Warning:</strong> Without a primary storage account, backups cannot run for
              this warehouse.
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {addStorage.isError && (
        <div className='flex items-start p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg'>
          <svg
            className='h-5 w-5 text-red-500 mt-0.5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <div className='ml-3'>
            <p className='text-sm text-red-800 dark:text-red-200'>
              Failed to add storage account:{' '}
              {(addStorage.error as Error)?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      )}

      {/* Selected Account Summary */}
      {selectedAccount && (
        <div className='p-4 bg-[var(--color-bg-subtle)] rounded-lg'>
          <h4 className='text-sm font-medium text-[var(--color-text-primary)] mb-2'>Summary</h4>
          <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
            <dt className='text-[var(--color-text-secondary)]'>Storage Account</dt>
            <dd className='text-[var(--color-text-primary)] font-medium'>{selectedAccount.name}</dd>

            <dt className='text-[var(--color-text-secondary)]'>Location</dt>
            <dd className='text-[var(--color-text-primary)]'>{selectedAccount.location}</dd>

            <dt className='text-[var(--color-text-secondary)]'>Purpose</dt>
            <dd className='text-[var(--color-text-primary)]'>
              {purposeOptions.find((p) => p.value === purpose)?.label}
            </dd>

            <dt className='text-[var(--color-text-secondary)]'>Primary</dt>
            <dd className='text-[var(--color-text-primary)]'>
              {hasPrimaryMapping ? 'No (existing primary)' : setAsPrimary ? 'Yes' : 'No'}
            </dd>
          </dl>
        </div>
      )}

      {/* Form Actions */}
      <div className='flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]'>
        <button
          type='button'
          onClick={onCancel}
          disabled={addStorage.isPending}
          className='px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)] disabled:opacity-50'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={!selectedAccountId || addStorage.isPending}
          className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {addStorage.isPending ? (
            <span className='flex items-center'>
              <svg
                className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              Adding...
            </span>
          ) : (
            'Add Storage Account'
          )}
        </button>
      </div>
    </form>
  );
}

export default AddWarehouseStorageAccountForm;
